import translate from "@insite/client-framework/Translate";
import TextField from "@insite/mobius/TextField/TextField";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import React, { RefObject } from "react";

interface OwnProps {
    onChange: EventEx;
    update: EventEx;
    format: string;
    label: string;
    required: boolean;
    initialValue: string;
    hideRequiredAsterisk: boolean | undefined;
}

export interface PhoneNumberState {
    error: string;
    value: string;
}

type OwnState = OwnProps & PhoneNumberState;
export class PhoneNumberField extends React.Component<OwnProps, OwnState> {
    requiredFieldMessage = translate("This is a required field.");
    phoneFieldMessage = translate("Enter a valid phone number.");
    phoneRegex = new RegExp("[0-9]{3}-[0-9]{3}-[0-9]{4}");
    constructor(props: OwnProps) {
        super(props);
        this.state = {
            label: props.label,
            required: props.required,
            onChange: props.onChange,
            format: props.format ? props.format : "000-000-0000",
            initialValue: props.initialValue,
            value: props.initialValue ? props.initialValue : "",
            error: "",
            update: this.props.update,
            hideRequiredAsterisk: this.props.hideRequiredAsterisk,
        };
        this.buildEmptyRegex = this.buildEmptyRegex.bind(this);
        this.onChange = this.onChange.bind(this);
        this.update = this.update.bind(this);
        if (this.state.update) {
            this.state.update.subscribe(async () => {
                await this.update(this.state.value);
            });
        }
    }

    buildEmptyRegex() {
        let result = "";
        for (let i = 0; i < this.state.format.length; i++) {
            if (this.state.format[i] < "0" || this.state.format[i] > "9") {
                result += this.state.format[i];
            }
        }
        return result;
    }

    static getDigits(value: string) {
        let digits = "";
        for (let i = 0; i < value.length; i++) {
            if (value[i] >= "0" && value[i] <= "9") {
                digits += value[i];
            }
        }
        return digits;
    }

    static formatPhoneNumber(value: string, format: string) {
        const digits = PhoneNumberField.getDigits(value);
        let currentDigitIndex = 0;
        let currentSeparatorIndex = 0;
        let formattedPhoneNumber = "";
        for (let i = 0; i < format.length; i++) {
            if (format[i] >= "0" && format[i] <= "9") {
                if (currentDigitIndex < digits.length) {
                    formattedPhoneNumber += digits[currentDigitIndex];
                }
                currentDigitIndex++;
            } else if (digits.length + currentSeparatorIndex >= formattedPhoneNumber.length) {
                formattedPhoneNumber += format[i];
                currentSeparatorIndex++;
            }
        }
        return formattedPhoneNumber;
    }

    async update(formattedPhoneNumber: string) {
        await this.setState({ value: formattedPhoneNumber });
        const digits = PhoneNumberField.getDigits(formattedPhoneNumber);

        if (digits.length === 0) {
            if (this.state.required) {
                await this.setState({ error: this.requiredFieldMessage });
            } else {
                await this.setState({ error: "" });
            }
        } else if (!this.phoneRegex.test(this.state.value)) {
            await this.setState({ error: this.phoneFieldMessage });
        } else {
            await this.setState({ error: "" });
        }

        await this.state.onChange.emitAsync(this.state);
    }

    async onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const unfilteredValue = event.target.value;
        let digits = PhoneNumberField.getDigits(unfilteredValue);
        const oldDigits = PhoneNumberField.getDigits(this.state.value);

        if (digits.length === oldDigits.length && unfilteredValue.length !== this.state.value.length) {
            digits = digits.substr(0, digits.length - 1);
        }

        const formattedPhoneNumber = PhoneNumberField.formatPhoneNumber(digits, this.state.format);

        await this.update(formattedPhoneNumber);
    }

    render() {
        return (
            <TextField
                label={this.state.label}
                value={this.state.value}
                onChange={this.onChange}
                error={this.state.error}
                required={this.state.required && !this.state.hideRequiredAsterisk}
            />
        );
    }
}

export default PhoneNumberField;
